import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import trackVisit from './utils/trackVisit';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import AdoptACow from './pages/AdoptACow';
import AboutUs from './pages/AboutUs';
import Management from './pages/Management';
import IndianBreeds from './pages/IndianBreeds';
import Activities from './pages/Activities';
import Donations from './pages/Donations';
import MyDonations from './pages/MyDonations';
import Gallery from './pages/Gallery';
import Videos from './pages/Videos';
import Slokas from './pages/Slokas';
import FAQs from './pages/FAQs';
import Login from './pages/Login';
import DonationSuccess from './pages/DonationSuccess';
import DonationFailure from './pages/DonationFailure';
import DonationReceiptPage from './pages/DonationReceiptPage';
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import AdminLoginModal from './admin/AdminLoginModal';
import LoginModal from './components/LoginModal';
import ChatWidget from './components/ChatWidget';
import { useAuth } from './context/AuthContext';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loader"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem('adminToken');
  return adminToken ? children : <Navigate to="/admin/login" />;
};

// Once an admin is signed in, keep them in the admin area — don't let them
// wander onto the public/guest-facing pages of the site.
const GuestOnlyRoute = ({ children }) => {
  const adminToken = localStorage.getItem('adminToken');
  return adminToken ? <Navigate to="/admin" /> : children;
};

// The floating chat bubble is a distraction on a page meant for
// printing/scanning, and can overlap the receipt content on mobile.
const ChatWidgetGate = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/donation/receipt/')) return null;
  return <ChatWidget />;
};

const PublicLayout = ({ children }) => {
  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) return <Navigate to="/admin" />;
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
};

export default function App() {
  useEffect(() => { trackVisit(); }, []);

  return (
    <Router>
      <LoginModal />
      <AdminLoginModal />
      <ChatWidgetGate />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
        <Route path="/adopt-a-cow" element={<PublicLayout><AdoptACow /></PublicLayout>} />
        <Route path="/about-us" element={<PublicLayout><AboutUs /></PublicLayout>} />
        <Route path="/management" element={<PublicLayout><Management /></PublicLayout>} />
        <Route path="/indian-breeds" element={<PublicLayout><IndianBreeds /></PublicLayout>} />
        <Route path="/activities" element={<PublicLayout><Activities /></PublicLayout>} />
        <Route path="/donations" element={<PublicLayout><Donations /></PublicLayout>} />
        <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
        <Route path="/videos" element={<PublicLayout><Videos /></PublicLayout>} />
        <Route path="/slokas" element={<PublicLayout><Slokas /></PublicLayout>} />
        <Route path="/faqs" element={<PublicLayout><FAQs /></PublicLayout>} />
        <Route path="/login" element={<GuestOnlyRoute><Login /></GuestOnlyRoute>} />
        <Route path="/donation/success" element={<PublicLayout><DonationSuccess /></PublicLayout>} />
        <Route path="/donation/failure" element={<PublicLayout><DonationFailure /></PublicLayout>} />
        <Route path="/donation/receipt/:txnId" element={<DonationReceiptPage />} />

        {/* Protected user routes */}
        <Route path="/my-donations" element={<ProtectedRoute><PublicLayout><MyDonations /></PublicLayout></ProtectedRoute>} />

        {/* Admin routes */}
        <Route path="/admin/login" element={<GuestOnlyRoute><AdminLogin /></GuestOnlyRoute>} />
        <Route path="/admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}
